// The Admin home's "Needs action" strip: five queues, each a link to the page that clears it.
// Every count comes from its own source. A source that fails or has not answered shows a dash and
// is left out of the total: a failed fetch must never read as "nothing needs doing".
import React from 'react';
import { Link } from 'react-router-dom';

const isNum = (n) => typeof n === 'number' && Number.isFinite(n);
const money = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function oldestWaiting(pending) {
  const times = (Array.isArray(pending) ? pending : [])
    .map((c) => new Date(c.created_at).getTime()).filter((t) => Number.isFinite(t));
  if (!times.length) return null;
  return Math.max(0, Math.floor((Date.now() - Math.min(...times)) / 86400000));
}

export function buildQueues({ pending, flags, payments, issues, candidates }) {
  const waited = oldestWaiting(pending);
  const failed = payments?.status_counts?.failed;
  return [
    { key: 'approvals', label: 'Coach approvals', tone: 'amber', to: '/admin/approvals', cta: 'Review',
      count: Array.isArray(pending) ? pending.length : null,
      sub: waited == null ? 'Coaches waiting for review' : `Oldest waiting ${plural(waited, 'day', 'days')}` },
    { key: 'flags', label: 'Flagged messages', tone: 'red', to: '/admin/flags', cta: 'Open queue',
      count: isNum(flags?.pending_flags) ? flags.pending_flags : null, sub: 'Waiting for a decision' },
    { key: 'payments', label: 'Failed payments', tone: 'red', to: '/admin/payments', cta: 'Payments',
      count: isNum(failed) ? failed : null,
      sub: isNum(payments?.failed_cents) && payments.failed_cents > 0 ? `${money(payments.failed_cents)} not collected` : 'None in this window' },
    { key: 'logins', label: 'Login problems', tone: 'amber', to: '/admin/login-problems', cta: 'See who',
      count: isNum(issues?.count) ? issues.count : null,
      sub: isNum(issues?.count)
        ? `${isNum(issues.locked) ? issues.locked : 0} locked · ${plural(isNum(issues.bad_email) ? issues.bad_email : 0, 'bad email', 'bad emails')}`
        : 'Athletes who cannot sign in' },
    { key: 'duplicates', label: 'Possible duplicates', tone: 'amber', to: '/admin/merge', cta: 'Merge',
      count: isNum(candidates?.count) ? candidates.count : null,
      sub: candidates?.example ? `e.g. ${candidates.example}` : 'Same phone and name' },
  ];
}

export default function NeedsActionStrip(props) {
  const queues = buildQueues(props);
  const loaded = queues.filter((q) => q.count != null);
  const total = loaded.reduce((n, q) => n + q.count, 0);
  return (
    <section className="adm__card adm-needs" aria-label="Needs action">
      <div className="adm-needs__head" data-testid="admin_queue_total">
        <strong className="adm-needs__title">Needs action</strong>
        <span className="adm-needs__sub">
          {loaded.length ? `${plural(total, 'item', 'items')} · ${queues.length} queues` : 'Counts unavailable'}
        </span>
      </div>
      {queues.map((q) => {
        const urgent = q.count != null && q.count > 0;
        return (
          <Link key={q.key} to={q.to} className="adm-queue" data-testid={`admin_queue_${q.key}`}>
            <span className="adm-queue__label">{q.label}</span>
            <span className={`adm-queue__count ${urgent ? `adm-queue__count--${q.tone}` : 'adm-queue__count--quiet'}`}>
              {q.count == null ? '—' : q.count}
            </span>
            <span className="adm-queue__sub">{q.sub}</span>
            <span className="adm-queue__cta">{q.cta} →</span>
          </Link>
        );
      })}
    </section>
  );
}
